/**
 * KAN-788, KAN-880, KAN-883: фильтры таблицы CRM в адресе, кроме периода и
 * общих фильтров: атрибут (?attr=&attr_value=), город / организация
 * (?city=&company=&company_id=), признаки lead / idle / event (lib/crmTableShortcuts).
 * Вынесено из CrmTableSection: компонент только рисует.
 * KAN-910: чтение и запись адреса - useUrlState (replace).
 */
import { useCallback, useMemo } from 'react';
import { useUrlState } from '@/hooks/useUrlState';
import type { CrmLocationFilterValue } from '@/components/crm/CrmLocationFilters';
import type { CrmAttributeFilter } from '@/lib/crmAttributeFilter';
import { CRM_SHORTCUT_PARAM, parseCrmTableFlags } from '@/lib/crmTableShortcuts';

import { CRM_ATTRIBUTE_PARAM, CRM_LOCATION_PARAM } from '@/lib/crmTableUrlParams';

const LOCATION_PARAMS = CRM_LOCATION_PARAM;
const ATTR = CRM_ATTRIBUTE_PARAM.attr;
const ATTR_VALUE = CRM_ATTRIBUTE_PARAM.value;

export function useCrmTableUrlFilters() {
  const { params, update } = useUrlState();
  const attr = params.get(ATTR);
  const attrValue = params.get(ATTR_VALUE);
  const city = params.get(LOCATION_PARAMS.city);
  const company = params.get(LOCATION_PARAMS.company);
  const companyId = params.get(LOCATION_PARAMS.companyId);
  const location: CrmLocationFilterValue = useMemo(() => ({ city, company, companyId }), [city, company, companyId]);

  const eventParam = params.get(CRM_SHORTCUT_PARAM.event);
  const leadParam = params.get(CRM_SHORTCUT_PARAM.lead);
  const idleParam = params.get(CRM_SHORTCUT_PARAM.idle);
  const flags = useMemo(
    () =>
      parseCrmTableFlags(
        new URLSearchParams({
          [CRM_SHORTCUT_PARAM.event]: eventParam ?? '',
          [CRM_SHORTCUT_PARAM.lead]: leadParam ?? '',
          [CRM_SHORTCUT_PARAM.idle]: idleParam ?? '',
        })
      ),
    [eventParam, leadParam, idleParam]
  );

  /** Устаревший атрибут (сервер ответил 400 по attr): фильтр снимается. */
  const clearAttribute = useCallback(
    () =>
      update((next) => {
        next.delete(ATTR);
        next.delete(ATTR_VALUE);
      }),
    [update]
  );

  const setAttribute = useCallback(
    (value: CrmAttributeFilter | null) =>
      update((next) => {
        next.delete(ATTR);
        next.delete(ATTR_VALUE);
        if (value) {
          next.set(ATTR, value.attr);
          if (value.value) next.set(ATTR_VALUE, value.value);
        }
      }),
    [update]
  );

  const setLocation = useCallback(
    (value: Partial<CrmLocationFilterValue>) =>
      update((next) => {
        (Object.keys(value) as (keyof CrmLocationFilterValue)[]).forEach((key) => {
          const v = value[key];
          if (v) next.set(LOCATION_PARAMS[key], v);
          else next.delete(LOCATION_PARAMS[key]);
        });
      }),
    [update]
  );

  const resetLocation = useCallback(
    () => setLocation({ city: null, company: null, companyId: null }),
    [setLocation]
  );

  /** KAN-883: период снова по дате взятия (убрать ?event=). */
  const clearEvent = useCallback(() => update((next) => next.delete(CRM_SHORTCUT_PARAM.event)), [update]);

  return {
    attr,
    attrValue,
    location,
    flags,
    clearAttribute,
    setAttribute,
    setLocation,
    resetLocation,
    clearEvent,
  };
}

export type CrmTableUrlFilters = ReturnType<typeof useCrmTableUrlFilters>;
